import * as THREE from 'three';
import arena from './clubArena.json';
import { validateSurfaces, shapeOf } from '../core/surfaces.js';
import { createCrowdBodies } from './crowd.js';
import { createBar } from './clubBar.js';
import { arenaShape, brickTexture, buildLighting, createKit, drawnTexture } from './kit.js';

/**
 * A club basement: the smallest floor in the game, and the most crowded.
 *
 * Five metres by one point eight to fight on. A bar along the back wall, a
 * ceiling at 2.7 m with pipes running under it, two speaker stacks at the bar's
 * ends and two steel posts holding the floor above up. The crowd is pressed in
 * on three sides — the bar is the fourth — and the camera watches from the
 * stairwell, the one open side, over a front row crouched down so the people
 * behind them can see.
 *
 * Everything hard is a shape the collision already knew: the bar and the
 * speakers are `box`es, the posts `column`s, the walls `plane`s. All of it is
 * outside the floor, which here is a narrow thing: half a metre of floor
 * between the walkable edge and the bar.
 *
 * **The signature is through the bar.** A hit against it knocks a section's
 * panel askew and puts the bottles near it over; a finish goes through —
 * panel gone, counter top folded into a V, bottles flung, splinters on the
 * floor — and the bar answers `onStruck` with a resting place inside its own
 * footprint, so the body ends up in the wreck rather than bounced off the
 * front of it. The host eases it there once the rebound has played out (see
 * `SETTLE` in `index.js`): moved during the rebound, it would be shoved back
 * out through the front and hit the bar a second time.
 */

export { arena as CLUB_ARENA };

const WALL_THICKNESS = 0.25;

export function createClubArena(config = arena, { onWarn, crowd = null } = {}) {
  try {
    validateSurfaces(config.surfaces);
  } catch (error) {
    onWarn?.(error.message);
  }

  const group = new THREE.Group();
  group.name = `arena-${config.id}`;
  const kit = createKit(group);
  const colors = config.palette ?? {};
  const tone = (key, fallback) => new THREE.Color(colors[key] ?? fallback);
  const room = config.room;
  const roomWidth = room.maxX - room.minX;
  const depth = room.stairwell - room.minZ;
  const midX = (room.minX + room.maxX) / 2;

  // --- floor and ceiling ------------------------------------------------------
  const floorTexture = drawnTexture(256, 256, (ctx, w, h, random) => {
    ctx.fillStyle = colors.floor ?? '#1d1b1f';
    ctx.fillRect(0, 0, w, h);
    // Scuffs, and the sheen of something spilled.
    for (let i = 0; i < 1800; i++) {
      ctx.fillStyle = random() < 0.5 ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.1)';
      ctx.fillRect(random() * w, random() * h, 1 + random() * 3, 1 + random());
    }
  }, { seed: 41, repeat: [roomWidth / 2, depth / 2] });
  if (floorTexture) kit.keep(floorTexture);
  kit.box(roomWidth, 0.2, depth, kit.material({
    color: floorTexture ? 0xffffff : tone('floor', '#1d1b1f'),
    map: floorTexture,
    roughness: 0.55,
    metalness: 0.1,
  }), { x: midX, y: -0.1, z: (room.minZ + room.stairwell) / 2, name: 'club-floor', casts: false });

  // Over the room only: the stairwell side is open, which is where the camera
  // stands, and a slab over it would be the first thing in shot.
  const ceilingDepth = room.maxZ - room.minZ;
  kit.box(roomWidth, 0.25, ceilingDepth, kit.material({ color: tone('ceiling', '#151316'), roughness: 1 }), {
    x: midX,
    y: room.ceiling + 0.125,
    z: (room.minZ + room.maxZ) / 2,
    name: 'ceiling',
    casts: false,
  });
  for (const pipe of config.pipes ?? []) {
    const metal = kit.material({ color: pipe.color ?? '#7d8186', roughness: 0.45, metalness: 0.6 });
    if (pipe.axis === 'x') {
      kit.cylinder(pipe.radius, roomWidth, metal, { x: midX, y: pipe.y, z: pipe.z, rotZ: Math.PI / 2, segments: 12, name: 'pipe', casts: false });
    } else {
      kit.cylinder(pipe.radius, ceilingDepth, metal, { x: pipe.x, y: pipe.y, z: (room.minZ + room.maxZ) / 2, rotX: Math.PI / 2, segments: 12, name: 'pipe', casts: false });
    }
  }

  // The stairs down into it, against the east wall, out of the camera's way.
  const stair = kit.material({ color: tone('stair', '#2f2b2c'), roughness: 0.9 });
  const steps = 12;
  const rise = room.ceiling / steps;
  const run = (room.stairwell - room.maxZ) / steps;
  for (let i = 0; i < steps; i++) {
    kit.box(1.3, rise * (i + 1), run, stair, {
      x: room.maxX - 0.65,
      y: (rise * (i + 1)) / 2,
      z: room.maxZ + run * (i + 0.5),
      name: 'stair',
    });
  }

  // --- the walls, the speakers, the posts, the bar ---------------------------
  const brick = brickTexture({ brick: colors.wall, mortar: colors.mortar, seed: 43 });
  if (brick) kit.keep(brick);
  const built = [];
  let bar = null;
  for (const surface of config.surfaces) {
    const height = surface.height ?? room.ceiling;
    if (surface.look === 'bar') {
      bar = createBar(kit, surface, { palette: colors, config: config.bar, damage: config.damage });
      group.add(bar.object3D);
      built.push({ surface, mesh: bar.object3D });
      continue;
    }
    if (shapeOf(surface) === 'column') {
      const steel = kit.material({ color: '#3b3e42', roughness: 0.5, metalness: 0.7 });
      built.push({ surface, mesh: kit.cylinder(surface.radius, height, steel, { x: surface.center.x, y: height / 2, z: surface.center.z, segments: 12, name: `post-${surface.id}` }) });
      continue;
    }
    if (shapeOf(surface) === 'box') {
      const { center, halfX, halfZ } = surface;
      const cabinet = kit.material({ color: tone('speaker', '#151517'), roughness: 0.85 });
      const cone = kit.material({ color: '#0a0a0b', roughness: 0.5, metalness: 0.2 });
      const mesh = kit.box(halfX * 2, height, halfZ * 2, cabinet, { x: center.x, y: height / 2, z: center.z, name: `speaker-${surface.id}` });
      // Two drivers on the face that looks at the floor.
      for (const [y, r] of [[height * 0.3, halfX * 0.6], [height * 0.72, halfX * 0.35]]) {
        kit.cylinder(r, 0.03, cone, { x: center.x, y, z: center.z + halfZ + 0.01, rotX: Math.PI / 2, segments: 18, name: `speaker-cone-${surface.id}`, casts: false });
      }
      built.push({ surface, mesh });
      continue;
    }
    const { from, to, normal } = surface;
    const span = Math.hypot(to.x - from.x, to.z - from.z) + 0.3;
    const rotY = Math.atan2(-normal.x, -normal.z);
    const wallMap = brick ? kit.keep(brick.clone()) : null;
    if (wallMap) {
      wallMap.needsUpdate = true;
      wallMap.repeat.set(span, height);
    }
    const mesh = kit.box(span, height, WALL_THICKNESS, kit.material({ color: wallMap ? 0xffffff : tone('wall', '#2a2427'), map: wallMap, roughness: 0.95 }), {
      x: (from.x + to.x) / 2 - normal.x * (WALL_THICKNESS / 2),
      y: height / 2,
      z: (from.z + to.z) / 2 - normal.z * (WALL_THICKNESS / 2),
      rotY,
      name: `wall-${surface.id}`,
    });
    built.push({ surface, mesh });
  }

  // A neon sign over the back bar, the room's own name for itself.
  const neon = drawnTexture(512, 128, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.font = 'bold 78px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = colors.neon ?? '#ff3d8b';
    ctx.shadowBlur = 18;
    ctx.fillStyle = '#ffe3ef';
    ctx.fillText('DOWNSTAIRS', w / 2, h / 2);
  });
  if (neon) {
    kit.keep(neon);
    kit.box(2.6, 0.65, 0.01, kit.material({
      map: neon,
      transparent: true,
      emissive: tone('neon', '#ff3d8b'),
      emissiveMap: neon,
      emissiveIntensity: 1.6,
      depthWrite: false,
    }), { x: 0, y: 2.25, z: room.minZ + 0.02, name: 'neon-sign', casts: false });
  }

  kit.mergeStatic(group, { exclude: (mesh) => /^bar-(panel|top)/.test(mesh.name) });
  group.add(buildLighting(config.lighting));

  const bodies =
    crowd && config.crowd
      ? createCrowdBodies(crowd, { config: config.crowd, surfaces: config.surfaces, palette: colors })
      : null;
  if (bodies) group.add(bodies.object3D);

  return arenaShape(config, {
    group,
    kit,
    extra: { crowd: bodies, walls: built, bar },
    update(dt) {
      bodies?.update(dt);
    },
    dispose() {
      bodies?.dispose();
    },
    reset() {
      bar?.reset();
    },
    onStruck(event) {
      if (event.surface !== bar?.id) return null;
      return bar.hit(event);
    },
  });
}
